/**
 * Afuna Aromatics — "Add the Duo" control for the blend-family pages.
 * R2 Phase 3.
 *
 * Four honest states, driven by the live cart (/cart.js):
 *   - neither format in cart  -> button "Add the Duo"      -> adds one Mood Mist + one Body Oil
 *   - exactly one format       -> button "Complete the Duo" -> adds only the missing format
 *   - both formats present     -> status "Duo in cart" + button "Add another duo" (adds one of each)
 *   - a format is unavailable  -> never claims a complete duo; shows the available option only
 *
 * Uses the blend's existing variants only. No bundle product, no duplicate
 * product, no new variant, no discount, no new Shopify File. Nothing is
 * added to the cart without an explicit click, and the visible count /
 * status / announcement only change AFTER Shopify confirms the add.
 *
 * Progressive enhancement: with JS off (or on failure) the two format
 * links above still work and no false state is shown.
 */
(function () {
  'use strict';

  var CART_EVENT = 'shopify:cart:lines-update';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    document.querySelectorAll('[data-aa-blend-duo]').forEach(function (root) {
      try {
        setup(root);
      } catch (err) {
        console.warn('[aa-blend-duo] setup failed:', err);
      }
    });
  });

  function setup(root) {
    var button = root.querySelector('[data-duo-button]');
    var status = root.querySelector('[data-duo-status]');
    if (!button) return; // duo not ready (a format unavailable) — server already rendered the note

    var sprayId = parseInt(root.getAttribute('data-spray-variant-id') || '', 10);
    var oilId = parseInt(root.getAttribute('data-oil-variant-id') || '', 10);
    var sprayAvailable = root.getAttribute('data-spray-available') === 'true';
    var oilAvailable = root.getAttribute('data-oil-available') === 'true';
    if (isNaN(sprayId) || isNaN(oilId)) return;

    var busy = false;

    function setStatus(text) {
      if (!status) return;
      if (text) {
        status.textContent = text;
        status.hidden = false;
      } else {
        status.textContent = '';
        status.hidden = true;
      }
    }

    function countsFromCart(cart) {
      var spray = 0;
      var oil = 0;
      if (cart && Array.isArray(cart.items)) {
        cart.items.forEach(function (item) {
          var id = Number(item.id) || Number(item.variant_id);
          if (id === sprayId) spray += Number(item.quantity) || 0;
          if (id === oilId) oil += Number(item.quantity) || 0;
        });
      }
      return { spray: spray, oil: oil };
    }

    function applyState(counts) {
      var hasSpray = counts.spray > 0;
      var hasOil = counts.oil > 0;

      if (!sprayAvailable || !oilAvailable) {
        // Cannot honestly offer a complete duo.
        button.hidden = true;
        setStatus('One format is currently unavailable, so the full duo can’t be added right now.');
        return;
      }
      button.hidden = false;
      button.disabled = false;
      button.removeAttribute('aria-disabled');

      if (hasSpray && hasOil) {
        button.textContent = 'Add another duo';
        setStatus('Both formats are in your cart.');
      } else if (hasSpray || hasOil) {
        button.textContent = 'Complete the Duo';
        setStatus(
          hasSpray
            ? 'The Mood Mist is in your cart. Complete the Duo adds the Body Oil.'
            : 'The Body Oil is in your cart. Complete the Duo adds the Mood Mist.'
        );
      } else {
        button.textContent = 'Add the Duo';
        setStatus('');
      }
      button.dataset.duoMode = hasSpray && hasOil ? 'another' : hasSpray || hasOil ? 'complete' : 'add';
      button.dataset.missing = hasSpray && !hasOil ? 'oil' : hasOil && !hasSpray ? 'spray' : 'both';
    }

    function refresh() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { applyState(countsFromCart(cart || {})); })
        .catch(function () { /* leave last known state */ });
    }

    function itemsToAdd() {
      var mode = button.dataset.duoMode;
      if (mode === 'complete') {
        return button.dataset.missing === 'oil'
          ? [{ id: oilId, quantity: 1 }]
          : [{ id: sprayId, quantity: 1 }];
      }
      // 'add' or 'another' -> one of each
      return [
        { id: sprayId, quantity: 1 },
        { id: oilId, quantity: 1 },
      ];
    }

    function announceCartUpdate(cart) {
      // Let the cart drawer / cart icon react. Only fired AFTER a confirmed
      // successful add. cart-icon.js reads `event.promise` and resolves it to
      // `{ cart, detail }`, then uses `cart.totalQuantity ?? detail.itemCount`.
      // /cart.js returns the total in `item_count`, so map it into BOTH keys of
      // the theme's CartLinesUpdateResult shape — the previous
      // `{ detail: { cart } }` shape left both reads undefined, so the header
      // badge silently stayed stale until the cart was opened.
      var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
      try {
        var evt = new CustomEvent(CART_EVENT, { bubbles: true });
        evt.action = 'add';
        evt.context = 'standard-action';
        evt.promise = Promise.resolve({
          cart: { id: cart && cart.token, totalQuantity: count },
          detail: { itemCount: count },
        });
        document.dispatchEvent(evt);
      } catch (e) { /* noop */ }
    }

    button.addEventListener('click', function () {
      if (busy || button.disabled || button.hidden) return;
      busy = true;
      var prevLabel = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      setStatus('Adding…');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: itemsToAdd() }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error('cart/add failed: ' + res.status);
          return res.json();
        })
        .then(function () {
          return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
            return r.ok ? r.json() : null;
          });
        })
        .then(function (cart) {
          busy = false;
          applyState(countsFromCart(cart || {}));
          announceCartUpdate(cart);
        })
        .catch(function (err) {
          console.warn('[aa-blend-duo]', err);
          busy = false;
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.textContent = prevLabel;
          setStatus('That didn’t go through — please try again.');
        });
    });

    document.addEventListener(CART_EVENT, function (event) {
      var run = function () { refresh(); };
      if (event && event.promise && typeof event.promise.finally === 'function') {
        event.promise.finally(run);
      } else {
        setTimeout(run, 60);
      }
    });

    refresh();
  }
})();
